import {ref} from "vue";
import {generateCUID} from "@/lib/utils";
import {Product} from "@/lib/keys";
import moment from "moment";

interface Snippet {
  '@context': 'https://schema.org/';
  '@type': string;

  [key: string]: any;
}

interface ProductOffer {
  '@type': 'Offer';
  price: number;
  priceCurrency: 'NOK' | string;
  itemCondition: string;
  availability: string;
  priceValidUntil?: string;
  url?: string;
  seller?: string;
  image: string;
}

interface ProductSnippet extends Snippet {
  '@type': 'Product';
  name: string;
  description: string;
  offers: ProductOffer[];
  sku: string | number;
  brand: {
    '@type': 'Brand';
    name: string;
  }
}

const createContext = <T extends Snippet>(attributes: Partial<T>): T => ({
  '@context': 'https://schema.org/',
  ...attributes
} as T)

const createOffer = (price: number, priceCurrency: string = 'NOK', additional: Partial<ProductOffer> = {}): ProductOffer => ({
  '@type': 'Offer',
  price,
  priceCurrency,
  itemCondition: 'https://schema.org/NewCondition',
  availability: 'https://schema.org/InStock',
  image: additional.image ?? 'https://snuspris.no/snus-can.svg',
  ...additional,
});

const createProduct = (name: string, description: string, offers: ProductOffer[] = [], additional: {} = {}): ProductSnippet => createContext<ProductSnippet>({
  '@type': 'Product',
  name,
  description,
  offers,
  ...additional,
})

export const useProductSnippet = (input: HTMLElement | null = null) => {
  const {element, setContents: innerSetContents} = useStructuredJsonSnippet(input);

  const setContents = (contents: ProductSnippet) => innerSetContents(contents);

  const setProduct = (product: Product, url: string,) => {
    const priceValidUntil = moment().add(1, 'day').toISOString();
    setContents(createProduct(
      product.name,
      product && product.description ? product.description.replaceAll('\n', '') : '',
      product.prices.map(conf => createOffer(conf.price, 'NOK', {
        url,
        priceValidUntil,
        seller: product.merchants.find(m => conf.merchant_id === m.id)?.name,
      })),
      {
        sku: product.slug,
        brand: {
          '@type': 'Brand',
          title: product.brand.name,
        }
      }
    ));
  }

  return {
    element,
    setContents,
    setProduct,
  }
}

export const useStructuredJsonSnippet = (input: HTMLElement | null = null) => {
  if (!input) {
    input = document.createElement('script');
    document.head.append(input)
  }

  if (!input.hasAttribute('data-snippet-id')) {
    input.setAttribute('data-snippet-id', generateCUID())
  }

  const element = ref<HTMLElement>(input)

  const initElement = () => element.value.setAttribute('type', 'application/ld+json');
  const setContents = (contents: Snippet) => element.value.textContent = JSON.stringify(contents)

  initElement();

  return {
    element,
    setContents,
  }
}
